import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Agent, CatalogTemplate } from '../types';
import { getCatalogTemplate, createAgent, API_BASE_URL } from '../api/client';

export function AgentCatalog() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [selectedTemplate, setSelectedTemplate] = useState<CatalogTemplate | null>(null);

  // Agent creation form
  const [agentName, setAgentName] = useState('');
  const [greeting, setGreeting] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [createdAgent, setCreatedAgent] = useState<Agent | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // If URL has templateId, load that template for creation
  useEffect(() => {
    if (templateId) {
      getCatalogTemplate(templateId).then((t) => {
        setSelectedTemplate(t);
        setAgentName('');
        setGreeting('');
        setCustomPrompt('');
        setFieldValues({});
        setCreatedAgent(null);
        setError(null);
      }).catch(() => navigate('/templates'));
    } else {
      setSelectedTemplate(null);
    }
  }, [templateId, navigate]);

  const handleCreate = async () => {
    if (!selectedTemplate || !agentName.trim()) return;

    for (const field of selectedTemplate.customFields) {
      if (field.required && !fieldValues[field.key]?.trim()) {
        setError(`Field "${field.label}" is required`);
        return;
      }
    }

    setCreating(true);
    setError(null);
    try {
      const agent = await createAgent({
        templateId: selectedTemplate.id,
        name: agentName.trim(),
        greeting: greeting.trim() || undefined,
        customPrompt: customPrompt.trim() || undefined,
        customFieldValues: fieldValues,
      });
      setCreatedAgent(agent);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setCreating(false);
    }
  };

  if (!templateId) {
    // No list view — redirect to /templates
    navigate('/templates');
    return null;
  }

  if (createdAgent) {
    // Same base a consumer (Eazyshow / live-video-chat) pastes into its settings — see AgentDetail
    const eazyshowUrl = `${API_BASE_URL}/std`;
    const copy = (label: string, value: string) => {
      navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    };
    const rows: [string, string][] = [
      ['API Key', createdAgent.apiKey],
      ['Agent ID', createdAgent.id],
      ['Eazyshow Integration URL', eazyshowUrl],
    ];
    return (
      <div style={styles.container}>
        <div style={styles.successBox}>
          <h3 style={{ margin: '0 0 8px' }}>
            Agent Created:{' '}
            <span style={styles.agentLink} title="Open agent page"
              onClick={() => navigate(`/agents/${createdAgent.id}`)}>{createdAgent.name}</span>
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: 13 }}>Save these credentials — you'll need them to connect:</p>
          {rows.map(([label, value]) => (
            <div key={label} style={{ marginBottom: 10 }}>
              <div style={styles.fieldLabel}>{label}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={styles.apiKeyCode}>{value}</code>
                <button style={styles.btnSecondary} onClick={() => copy(label, value)}>
                  {copied === label ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button style={styles.btn}
              onClick={() => copy('all', rows.map(([label, value]) => `${label}: ${value}`).join('\n'))}>
              {copied === 'all' ? 'Copied!' : 'Copy All'}
            </button>
            <button style={styles.btnSecondary} onClick={() => navigate(`/agents/${createdAgent.id}`)}>Open Agent</button>
            <button style={styles.btnSecondary} onClick={() => navigate('/agents')}>Go to My Agents</button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedTemplate) {
    return (
      <div style={styles.container}>
        <button style={styles.backBtn} onClick={() => navigate('/templates')}>&larr; Back to templates</button>
        <h2 style={styles.heading}>Create Agent</h2>

        <div style={styles.templateInfo}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: 15 }}>{selectedTemplate.name}</strong>
            <span style={{ fontSize: 12, color: '#888' }}>{selectedTemplate.language}</span>
          </div>
          {selectedTemplate.description && <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{selectedTemplate.description}</div>}
          {selectedTemplate.customFields.length > 0 && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
              Required fields: {selectedTemplate.customFields.filter((f) => f.required).map((f) => f.label).join(', ') || 'none'}
            </div>
          )}
        </div>

        <div style={styles.form}>
          <label style={styles.label}>
            Agent Name *
            <input style={styles.input} value={agentName} onChange={(e) => setAgentName(e.target.value)}
              placeholder="My Company Assistant" />
          </label>

          <label style={styles.label}>
            Greeting (optional)
            <input style={styles.input} value={greeting} onChange={(e) => setGreeting(e.target.value)}
              placeholder="Hello! How can I help you today?" />
          </label>

          {selectedTemplate.customFields.map((field) => (
            <label key={field.key} style={styles.label}>
              {field.label} {field.required && '*'}
              {field.type === 'textarea' ? (
                <textarea style={styles.textarea} rows={3}
                  placeholder={field.placeholder}
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues({ ...fieldValues, [field.key]: e.target.value })} />
              ) : field.type === 'select' ? (
                <select style={styles.select}
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues({ ...fieldValues, [field.key]: e.target.value })}>
                  <option value="">Select...</option>
                  {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input style={styles.input}
                  placeholder={field.placeholder}
                  value={fieldValues[field.key] || ''}
                  onChange={(e) => setFieldValues({ ...fieldValues, [field.key]: e.target.value })} />
              )}
            </label>
          ))}

          <label style={styles.label}>
            Additional Instructions (optional)
            <textarea style={styles.textarea} rows={3} value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Any extra context or preferences..." />
          </label>

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.btn} onClick={handleCreate} disabled={creating || !agentName.trim()}>
            {creating ? 'Creating...' : 'Create Agent'}
          </button>
        </div>
      </div>
    );
  }

  return <div style={styles.muted}>Loading...</div>;
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', flex: 1, overflowY: 'auto' },
  heading: { margin: '0 0 8px', fontSize: 18, color: '#1a1a1a' },
  backBtn: { background: 'none', border: 'none', color: '#4f46e5', fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#555', flex: 1 },
  input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none' },
  select: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, background: '#fff' },
  textarea: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' },
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  btnSecondary: { padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
  muted: { color: '#888', fontSize: 14 },
  templateInfo: { padding: '12px 16px', background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 16 },
  successBox: { padding: '24px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
  agentLink: { color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline' },
  apiKeyCode: { display: 'block', flex: 1, background: '#e2e8f0', padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' },
};
